import { API_URL } from "../../../config";

// Shared HTTP client for the three academic reports. Uses the same
// localStorage token as attoDeskApi. API_URL already ends in /api, so paths
// here start with the version prefix directly.
async function getJson<T>(path: string, params: Record<string, unknown> = {}): Promise<T> {
  const url = new URL(`${API_URL}/v1/reports${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) {
      v.forEach((x) => url.searchParams.append(`${k}[]`, String(x)));
    } else {
      url.searchParams.append(k, String(v));
    }
  });
  const res = await fetch(url.toString().replace(window.location.origin, ""), {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: any = new Error(json?.message || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return json as T;
}

// Trigger a file download from an authenticated GET. Uses a hidden anchor;
// the browser's download attribute names the file the way the server sent it.
export async function downloadFile(path: string, params: Record<string, unknown>): Promise<void> {
  const url = new URL(`${API_URL}/v1/reports${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) v.forEach((x) => url.searchParams.append(`${k}[]`, String(x)));
    else url.searchParams.append(k, String(v));
  });
  const res = await fetch(url.toString().replace(window.location.origin, ""), {
    method: "GET",
    headers: {
      Accept: "*/*",
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    },
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : "report";
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export type Meta = { current_page: number; per_page: number; total: number; last_page: number; from?: number; to?: number };
export type ListResponse<T> = { data: T[]; meta: Meta; summary: Record<string, number>; filters: Record<string, unknown> };

export const reportsApi = {
  studentSubjects:        (params: Record<string, unknown>) => getJson<ListResponse<any>>("/student-subjects", params),
  studentSubjectsSummary: (params: Record<string, unknown>) => getJson<Record<string, number>>("/student-subjects/summary", params),
  studentSubjectsOptions: () => getJson<{
    grades: { id: number; label: string }[];
    subjects: { id: number; label: string }[];
    locations: { id: number; label: string }[];
    study_modes: { value: string; label: string }[];
  }>("/student-subjects/options", {}),
  studentSubjectsCsv:     (params: Record<string, unknown>) => downloadFile("/student-subjects/export", params),
  studentSubjectsPdf:     (params: Record<string, unknown>) => downloadFile("/student-subjects/pdf", params),

  staffCapacity:        (params: Record<string, unknown>) => getJson<ListResponse<any>>("/staff-class-capacity", params),
  staffCapacityCsv:     (params: Record<string, unknown>) => downloadFile("/staff-class-capacity/export", params),
  staffCapacityPdf:     (params: Record<string, unknown>) => downloadFile("/staff-class-capacity/pdf", params),

  studentClasses:        (params: Record<string, unknown>) => getJson<ListResponse<any>>("/student-classes", params),
  studentClassesSummary: (params: Record<string, unknown>) => getJson<Record<string, number>>("/student-classes/summary", params),
  studentClassesOptions: () => getJson<{
    grades: { id: number; label: string }[];
    subjects: { id: number; label: string }[];
    staff: { id: number; label: string }[];
    locations: { id: number; label: string }[];
    study_modes: { value: string; label: string }[];
    days: { value: string; label: string }[];
    assignment_statuses: string[];
  }>("/student-classes/options", {}),
  studentClassesCsv:     (params: Record<string, unknown>) => downloadFile("/student-classes/export", params),
  studentClassesPdf:     (params: Record<string, unknown>) => downloadFile("/student-classes/pdf", params),

  regularClassSchedulePreview: (params: Record<string, unknown>) => getJson<{
    range: { from: string; to: string };
    summary: Record<string, number | any[]>;
    days: Array<{
      date: string;
      weekday: string;
      staff: Array<{
        staff_id: number | null;
        staff_name: string;
        classes: Array<{
          occurrence_id: number;
          class_id: number;
          scheduled_date: string;
          start_time: string | null;
          end_time: string | null;
          mode: string | null;
          location: string | null;
          location_id: number | null;
          staff_id: number | null;
          staff_name: string | null;
          capacity: number | null;
          enrolled_count: number;
          program_type: string | null;
          class_type: string | null;
          students: Array<{
            student_id: number;
            student_name: string;
            reference_id: string | null;
            grade: string | null;
            subject: string | null;
          }>;
        }>;
      }>;
    }>;
    filters: Record<string, unknown>;
  }>("/regular-class-schedule/preview", params),
  regularClassScheduleOptions: () => getJson<{
    staff: { id: number; label: string }[];
    locations: { id: number; label: string }[];
    study_modes: { value: string; label: string }[];
    program_types: string[];
    class_types: string[];
  }>("/regular-class-schedule/options", {}),
  regularClassSchedulePdf: (params: Record<string, unknown>) => downloadFile("/regular-class-schedule/pdf", params),
  regularClassScheduleCsv: (params: Record<string, unknown>) => downloadFile("/regular-class-schedule/csv", params),

  // ── Staff Timetable Report ────────────────────────────────────────────
  staffTimetablePreview: (params: Record<string, unknown>) => getJson<{
    range: { from: string; to: string; week_start: string; week_end: string };
    summary: Record<string, number>;
    staff: Array<{
      staff_id: number | null;
      staff_name: string;
      week_start: string;
      week_end: string;
      summary: {
        classes: number; students: number; teaching_hours: number;
        online: number; in_person: number; hybrid: number;
        zero_student: number; locations: string[];
      };
      days: Array<{
        date: string; weekday: string; day: string;
        classes: Array<{
          occurrence_id: number; class_id: number;
          start_time: string | null; end_time: string | null;
          mode: string | null; location: string | null; location_id: number | null;
          capacity: number | null; enrolled_count: number;
          program_type: string | null; class_type: string | null;
          students: Array<{
            student_id: number; student_name: string;
            reference_id: string | null; grade: string | null; subject: string | null;
          }>;
        }>;
      }>;
    }>;
    filters: Record<string, unknown>;
  }>("/staff-timetable/preview", params),
  staffTimetableOptions: (params: Record<string, unknown> = {}) => getJson<{
    grades: { id: number; label: string }[];
    subjects: { id: number; label: string }[];
    staff: { id: number; label: string; reference_id: string | null }[];
    locations: { id: number; label: string }[];
    study_modes: { value: string; label: string }[];
    program_types: string[];
    class_types: string[];
  }>("/staff-timetable/options", params),
  staffTimetableIndex: (params: Record<string, unknown>) => getJson<{
    data: Array<{ staff_id: number | null; staff_name: string; class_count: number }>;
    summary: Record<string, number>;
    range: { from: string; to: string; week_start: string; week_end: string };
  }>("/staff-timetable/index", params),
  staffTimetablePdf: (params: Record<string, unknown>) => downloadFile("/staff-timetable/pdf", params),
  staffTimetablePdfCompact: (params: Record<string, unknown>) => downloadFile("/staff-timetable/pdf-compact", params),
  staffTimetableCsv: (params: Record<string, unknown>) => downloadFile("/staff-timetable/csv", params),

  // ── Student Timetable Report ─────────────────────────────────────────
  studentTimetablePreview: (params: Record<string, unknown>) => getJson<{
    range: { from: string; to: string; week_start: string; week_end: string };
    summary: Record<string, number>;
    students: Array<{
      student_id: number;
      student_name: string;
      reference_id: string | null;
      grade: string | null;
      week_start: string;
      week_end: string;
      summary: {
        classes: number; hours: number; staff: number;
        subjects: string[]; online: number; in_person: number;
        locations: string[]; conflicts: number;
      };
      days: Array<{
        date: string; weekday: string; day: string;
        classes: Array<{
          occurrence_id: number; class_id: number;
          start_time: string | null; end_time: string | null;
          staff_id: number | null; staff_name: string | null;
          subject: string | null;
          mode: string | null;
          location: string | null; location_id: number | null;
        }>;
      }>;
    }>;
    filters: Record<string, unknown>;
  }>("/student-timetable/preview", params),
  studentTimetableOptions: (params: Record<string, unknown> = {}) => getJson<{
    grades: { id: number; label: string }[];
    subjects: { id: number; label: string }[];
    staff: { id: number; label: string }[];
    students: { id: number; label: string; reference_id: string | null }[];
    locations: { id: number; label: string }[];
    study_modes: { value: string; label: string }[];
    assignment_statuses: string[];
  }>("/student-timetable/options", params),
  studentTimetableIndex: (params: Record<string, unknown>) => getJson<{
    data: Array<{
      student_id: number;
      student_name: string;
      reference_id: string | null;
      grade: string | null;
      class_count: number;
    }>;
    summary: Record<string, number>;
    range: { from: string; to: string; week_start: string; week_end: string };
  }>("/student-timetable/index", params),
  studentTimetablePdf: (params: Record<string, unknown>) => downloadFile("/student-timetable/pdf", params),
  studentTimetableCsv: (params: Record<string, unknown>) => downloadFile("/student-timetable/csv", params),
};
